import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { DecodedJwt } from "@/types/token";
import { formatEpoch, relativeFromNow } from "@/utils/time";

import { CopyButton } from "./CopyButton";
import { JsonViewer } from "./JsonViewer";

type Props = {
  title: string;
  subtitle?: ReactNode;
  raw: string | null;
  decoded: DecodedJwt | null;
  extraRows?: Array<{ label: string; value: ReactNode }>;
  defaultOpen?: boolean;
};

/**
 * Accordion section showing a single token. Renders:
 *   - key facts (issuer, audience, expiry with countdown, scopes)
 *   - the decoded claims JSON
 *   - the decoded JWT header
 *   - the raw JWT string (with a copy button and expand toggle)
 *
 * Opaque (non-JWT) tokens degrade gracefully – only the raw value +
 * the caller-provided `extraRows` are shown.
 */
export function TokenSection({
  title,
  subtitle,
  raw,
  decoded,
  extraRows,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [showRaw, setShowRaw] = useState(false);

  const missing = !raw;

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {subtitle ? (
            <div className="mt-0.5 truncate text-xs text-slate-500">
              {subtitle}
            </div>
          ) : null}
        </div>
        {missing ? (
          <span className="chip bg-slate-100 text-slate-600">not available</span>
        ) : decoded ? (
          <span className="chip bg-brand-50 text-brand-700">JWT</span>
        ) : (
          <span className="chip bg-amber-50 text-amber-700">opaque</span>
        )}
      </button>

      {open && !missing ? (
        <div className="space-y-4 border-t border-slate-100 bg-white px-4 py-4">
          <Facts decoded={decoded} extraRows={extraRows} />

          {decoded ? (
            <>
              <Block title="Payload (decoded claims)">
                <JsonViewer data={decoded.payload} />
              </Block>
              <Block title="Header">
                <JsonViewer data={decoded.header} />
              </Block>
            </>
          ) : null}

          <Block
            title="Raw token"
            action={
              <>
                <button
                  type="button"
                  onClick={() => setShowRaw((s) => !s)}
                  className="btn-ghost !px-2 !py-1 text-xs"
                >
                  {showRaw ? "Hide" : "Reveal"}
                </button>
                <CopyButton value={raw ?? ""} label="Copy token" />
              </>
            }
          >
            <div className="break-all rounded border border-slate-200 bg-slate-50 p-3 font-mono text-[11.5px] text-slate-800">
              {showRaw ? raw : mask(raw)}
            </div>
          </Block>
        </div>
      ) : null}
    </div>
  );
}

function Facts({
  decoded,
  extraRows,
}: {
  decoded: DecodedJwt | null;
  extraRows?: Array<{ label: string; value: ReactNode }>;
}) {
  const payload = decoded?.payload;
  const expires = typeof payload?.exp === "number" ? payload.exp : undefined;
  const issued = typeof payload?.iat === "number" ? payload.iat : undefined;
  const scopes =
    typeof payload?.scp === "string"
      ? payload.scp.split(/\s+/).filter(Boolean)
      : [];

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
      {payload?.iss ? <Row label="Issuer" value={payload.iss} /> : null}
      {payload?.aud ? (
        <Row
          label="Audience"
          value={Array.isArray(payload.aud) ? payload.aud.join(", ") : payload.aud}
        />
      ) : null}
      {payload?.tid ? <Row label="Tenant (tid)" value={payload.tid} /> : null}
      {payload?.oid ? <Row label="Object id (oid)" value={payload.oid} /> : null}
      {payload?.sub ? <Row label="Subject (sub)" value={payload.sub} /> : null}
      {issued ? (
        <Row
          label="Issued"
          value={`${formatEpoch(issued)} (${relativeFromNow(issued)})`}
        />
      ) : null}
      {expires ? (
        <Row
          label="Expires"
          value={`${formatEpoch(expires)} (${relativeFromNow(expires)})`}
        />
      ) : null}
      {scopes.length > 0 ? (
        <Row
          label="Scopes (scp)"
          value={
            <div className="flex flex-wrap gap-1">
              {scopes.map((s) => (
                <span key={s} className="chip">
                  {s}
                </span>
              ))}
            </div>
          }
        />
      ) : null}
      {extraRows?.map((r) => (
        <Row key={r.label} label={r.label} value={r.value} />
      ))}
    </dl>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="shrink-0 font-semibold text-slate-500">{label}</dt>
      <dd className="min-w-0 truncate text-slate-700">{value}</dd>
    </div>
  );
}

function Block({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h4>
        <div className="flex items-center gap-2">{action}</div>
      </div>
      {children}
    </div>
  );
}

function mask(value: string | null): string {
  if (!value) return "";
  if (value.length <= 32) return "•".repeat(value.length);
  return value.slice(0, 12) + "••••••••••••" + value.slice(-6);
}
